/**
 * `Button`'s look (DESIGN_SYSTEM.md §4.1, §5.2): seven variants, two sizes, `fill`, `faded`, and the hover, focus,
 * press and disabled states. `root` is the `<button>` react-native-web emits, so it takes layout only
 * (§4.2); the paint is on `surface`.
 */
import type { ActionRole, Theme } from "@rista/ui/library/theme/theme";
import { defineVariants } from "@rista/ui/library/theme/variants";

/** How pale a disabled button draws; a `faded` one (still pressable) draws the same, so the two read alike. */
const DISABLED_OPACITY = 0.5;

/**
 * `dangerOutline` is a risky action that is NOT the one to reach for: red words in a pale red outline on white, beside
 * a primary that is the safe choice (My Orders' yes/no confirms, MY_ORDERS_V2_PLAN.md M19).
 */
export type ButtonVariant = "primary" | "secondary" | "danger" | "dangerOutline" | "ghost" | "link" | "outline";

const filled = (role: (t: Theme) => ActionRole) => (t: Theme) => ({
  surface: { backgroundColor: role(t).bg, borderColor: role(t).border },
  label: { color: role(t).fg },
  icon: { color: role(t).fg },
});

/** a variant's ground under a pointer or a finger; `link` has none (it underlines instead), `outline` is `secondary`'s */
const groundOf = (t: Theme, variant: ButtonVariant | undefined, state: "hover" | "pressed") => {
  const named = variant === "outline" ? "secondary" : variant;
  const action = named === "primary" || named === "danger" || named === "secondary" ? t.color.action[named] : null;
  if (action) return state === "hover" ? action.bgHover : action.bgPressed;
  return variant === "ghost" || variant === "dangerOutline" ? (state === "hover" ? t.color.surface.hover : t.color.surface.pressed) : undefined;
};

export const buttonLook = defineVariants({
  slots: ["root", "surface", "label", "icon"],
  base: (t) => ({
    // 🔴 a labelled action never shrinks: its label is one line, so shrinking only clips the verb (CLAUDE.md)
    root: { flexGrow: 0, flexShrink: 0, flexBasis: "auto" },
    surface: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: t.space.xs,
      borderRadius: t.radius.control,
      borderWidth: 1,
      borderColor: "transparent",
    },
    label: { ...t.type.bodyStrong, textAlign: "center" },
    icon: { color: t.color.text.primary },
  }),
  variants: {
    variant: {
      primary: filled((t) => t.color.action.primary),
      secondary: filled((t) => t.color.action.secondary),
      danger: filled((t) => t.color.action.danger),
      dangerOutline: (t) => ({
        surface: { backgroundColor: t.color.surface.raised, borderColor: t.color.status.danger.border },
        label: { color: t.color.status.danger.fg },
        icon: { color: t.color.status.danger.fg },
      }),
      ghost: (t) => ({ label: { color: t.color.text.primary }, icon: { color: t.color.text.primary } }),
      link: (t) => ({ label: { color: t.color.text.link }, icon: { color: t.color.text.link } }),
      // the secondary action's ground and border, its words in the link blue (v1's Redeem)
      outline: (t) => ({ ...filled((theme) => theme.color.action.secondary)(t), label: { color: t.color.text.link }, icon: { color: t.color.text.link } }),
    },
    size: {
      sm: (t) => ({ surface: { minHeight: 36, paddingHorizontal: t.space.lg, paddingVertical: t.space.sm } }),
      md: (t) => ({ surface: { minHeight: 44, paddingHorizontal: t.space.xxl, paddingVertical: t.space.md } }),
    },
    /** fill the width it is given: two equal halves of a dialog's footer, whose label wraps to two lines rather than be cut (decision 93) */
    fill: { true: () => ({ root: { flexGrow: 1, flexShrink: 1 }, label: { flexShrink: 1 } }) },
    /**
     * Drawn as pale as a disabled button but still enabled and pressable (RV4b, plan decision 60: "Create" looks pale while
     * something required is missing, and a press says what is missing). Opacity is a layout-slot value, as disabled's is.
     */
    faded: { true: () => ({ root: { opacity: DISABLED_OPACITY } }) },
  },
  compound: [
    // a text control: no ground, no border, no padding that would misalign it in a sentence
    { when: { variant: "link" }, style: () => ({ surface: { borderWidth: 0, paddingHorizontal: 0, paddingVertical: 0, minHeight: 0 } }) },
  ],
  states: {
    hovered: (t, v) =>
      v.variant === "link"
        ? { label: { textDecorationLine: "underline" } }
        : { surface: { backgroundColor: groundOf(t, v.variant as ButtonVariant, "hover") } },
    focused: (t, v) =>
      v.variant === "link" ? { label: { textDecorationLine: "underline" } } : { surface: { borderColor: t.color.border.focus } },
    pressed: (t, v) =>
      v.variant === "link" ? { label: { opacity: 0.75 } } : { surface: { backgroundColor: groundOf(t, v.variant as ButtonVariant, "pressed") } },
    disabled: () => ({ root: { opacity: DISABLED_OPACITY } }),
  },
  defaults: { variant: "secondary", size: "md" },
});
